import React from 'react';
import { Check, FileEdit, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

const WORKFLOW_STAGES = [
  { id: 'draft', label: 'Draft', icon: FileEdit },
  { id: 'pending_approval', label: 'Pending', icon: Clock },
  { id: 'approved', label: 'Approved', icon: Check },
  { id: 'scheduled', label: 'Scheduled', icon: Send },
  { id: 'published', label: 'Published', icon: CheckCircle },
];

const getStageIndex = (status) => {
  // Handle special case: approved posts with scheduled_at become 'scheduled'
  const index = WORKFLOW_STAGES.findIndex(s => s.id === status);
  return index === -1 ? 0 : index;
};

const StatusProgressBar = ({ status, size = 'default', showLabels = true }) => {
  const currentIndex = getStageIndex(status);
  const isFailed = status === 'failed';

  return (
    <div className="w-full" data-testid="status-progress-bar">
      <div className="flex items-center justify-between">
        {WORKFLOW_STAGES.map((stage, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const Icon = stage.icon;
          
          const sizeClasses = {
            small: 'w-6 h-6',
            default: 'w-8 h-8',
            large: 'w-10 h-10',
          };
          
          const iconSizeClasses = {
            small: 'h-3 w-3',
            default: 'h-4 w-4',
            large: 'h-5 w-5',
          };

          return (
            <React.Fragment key={stage.id}>
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    sizeClasses[size],
                    'rounded-full flex items-center justify-center transition-all duration-300',
                    isCompleted && 'bg-emerald-500 text-white',
                    isCurrent && !isFailed && 'bg-emerald-100 border-2 border-emerald-500 text-emerald-600',
                    isCurrent && isFailed && 'bg-red-100 border-2 border-red-500 text-red-600',
                    !isCompleted && !isCurrent && 'bg-slate-100 text-slate-400'
                  )}
                >
                  {isCompleted ? (
                    <Check className={iconSizeClasses[size]} />
                  ) : isCurrent && isFailed ? (
                    <AlertCircle className={iconSizeClasses[size]} />
                  ) : (
                    <Icon className={iconSizeClasses[size]} />
                  )}
                </div>
                {showLabels && (
                  <span
                    className={cn(
                      'text-xs mt-1.5 font-medium',
                      isCompleted && 'text-emerald-600',
                      isCurrent && !isFailed && 'text-emerald-600',
                      isCurrent && isFailed && 'text-red-600',
                      !isCompleted && !isCurrent && 'text-slate-400'
                    )}
                  >
                    {stage.label}
                  </span>
                )}
              </div>
              {index < WORKFLOW_STAGES.length - 1 && (
                <div
                  className={cn(
                    'flex-1 h-0.5 mx-2',
                    size === 'small' && 'mx-1',
                    index < currentIndex ? 'bg-emerald-500' : 'bg-slate-200'
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default StatusProgressBar;
